import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/site';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9slod14ne17ei0sdjgg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = (parsed && typeof (parsed as Record<string, unknown>).meta === 'object' && (parsed as Record<string, unknown>).meta
          ? ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message as string
          : '') || (parsed && (parsed as Record<string, unknown>).message as string) || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快回复！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、发展历程、用户口碑及联系方式。${siteInfo.description}`}
        keywords="蜜桃成熟时关于我们,联系我们,港片资讯,品牌介绍"
        canonicalUrl={`${siteInfo.url}/about`}
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: `关于${siteInfo.name}`,
          description: siteInfo.description,
          url: `${siteInfo.url}/about`,
          isPartOf: {
            '@type': 'WebSite',
            name: siteInfo.name,
            url: siteInfo.url,
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 pb-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Intro */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">关于{siteInfo.name}</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {siteInfo.name}创建于{siteInfo.foundingYear}年，是专注于香港经典电影《蜜桃成熟时》深度内容研究与分享的专业平台。我们致力于为港片爱好者和怀旧影迷提供最全面、最专业的电影资讯。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的团队由资深影评人、电影研究者及港片文化爱好者组成，通过严谨的学术态度和通俗的表达方式，为您解析这部经典作品的剧情脉络、演员表演、拍摄幕后及文化影响。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们相信，每一部经典电影都承载着时代的记忆与文化的密码。《蜜桃成熟时》作为90年代香港电影的重要作品，其艺术价值和文化意义值得被深入挖掘和持续讨论。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Classic%20Hong%20Kong%20cinema%20nostalgic%20atmosphere%20with%20film%20reels%20and%20warm%20lighting%2C%20vintage%20movie%20posters%20on%20wall%2C%20elegant%20golden%20and%20amber%20tones%2C%20cinematic%20photography%2C%20cozy%20film%20study%20room&width=600&height=400&seq=about-brand-01&orientation=landscape"
                  alt="蜜桃成熟时品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-eye-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">我们的愿景</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  成为华语地区最权威的经典港片内容平台，让每一部值得铭记的电影都有属于自己的深度解读空间。
                </p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-compass-3-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">我们的使命</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  挖掘经典电影的文化价值，连接影迷与电影之间更深层次的情感纽带，传承香港电影的文化记忆。
                </p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-heart-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">我们的价值观</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  坚持原创深度内容、尊重电影艺术精神、倡导理性文化讨论、拒绝低质标题党内容。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">用户口碑</h2>
              <p className="mt-3 text-foreground-600 text-sm">来自影迷和读者的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3 text-amber-500">
                  {[1,2,3,4,5].map((i) => (<i key={i} className="ri-star-fill text-sm"></i>))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  非常专业的内容！作为一个90年代港片迷，在这里找到了很多以前不了解的幕后故事和深度分析。文章质量很高，推荐给所有喜欢港片的朋友。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-amber-700">张</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">张先生</p>
                    <p className="text-xs text-foreground-400">资深影迷 · 广州</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3 text-amber-500">
                  {[1,2,3,4,5].map((i) => (<i key={i} className="ri-star-fill text-sm"></i>))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  做影视研究时偶然发现这个网站，内容深度超出预期。对蜜桃成熟时文化影响的分析角度很独特，参考文献详实，是很好的学术参考资料。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-amber-700">林</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">林教授</p>
                    <p className="text-xs text-foreground-400">电影研究者 · 北京</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3 text-amber-500">
                  {[1,2,3,4,5].map((i) => (<i key={i} className="ri-star-fill text-sm"></i>))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  作为80后，蜜桃成熟时是我们青春记忆的一部分。这个网站把这些美好的回忆用专业的方式保存下来，真的很感动。文章写得用心，排版也舒服。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-amber-700">王</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">王小姐</p>
                    <p className="text-xs text-foreground-400">文化博主 · 上海</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3 text-amber-500">
                  {[1,2,3,4,5].map((i) => (<i key={i} className="ri-star-fill text-sm"></i>))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  对李丽珍的表演分析非常精彩！以前只是觉得她漂亮，看完这里的文章才真正理解她在片中的表演层次和人物塑造。感谢这样的深度内容。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-amber-700">陈</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">陈先生</p>
                    <p className="text-xs text-foreground-400">电影爱好者 · 深圳</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">联系我们</h2>
              <p className="mt-3 text-foreground-600 text-sm">如有任何问题、建议或合作意向，欢迎通过以下方式联系我们</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-amber-600"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-950">{siteInfo.email}</p>
              </div>
              <div className="text-center p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-phone-line text-lg text-amber-600"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">联系电话</p>
                <p className="text-sm font-medium text-foreground-950">{siteInfo.phone}</p>
              </div>
              <div className="text-center p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-amber-50 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-amber-600"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">办公地址</p>
                <p className="text-sm font-medium text-foreground-950">{siteInfo.address}</p>
              </div>
            </div>

            <form
              ref={formRef}
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-xl border border-background-200/70 p-6"
            >
              {(formStatus === 'success' || formStatus === 'error') && (
                <div className={`mb-5 p-4 rounded-lg text-sm ${
                  formStatus === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {formMessage}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 <span className="text-red-400">*</span></label>
                  <input type="text" id="contact-name" name="name" required className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-amber-400/50 transition-colors" placeholder="请输入您的姓名" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 <span className="text-red-400">*</span></label>
                  <input type="email" id="contact-email" name="email" required className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-amber-400/50 transition-colors" placeholder="请输入您的邮箱" />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input type="text" id="contact-subject" name="subject" className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-amber-400/50 transition-colors" placeholder="请输入留言主题" />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 <span className="text-red-400">*</span></label>
                <textarea id="contact-message" name="message" required maxLength={500} rows={5} className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-amber-400/50 transition-colors resize-none" placeholder="请输入您的留言内容（最多500字）"></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多可输入500个字符</p>
              </div>
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>
              <button type="submit" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-send-plane-line"></i>
                发送留言
              </button>
            </form>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}